// theme.js
// Single source of truth for colors/spacing/radius/type — replaces the per-file cardStyle/
// inputStyle/labelStyle constants and one-off hex literals that had drifted across ~150 files
// (e.g. 4 different greens, 2 blues, 2 reds all doing the same job).
//
// 2026-08 "Ops Console" visual unification: every existing key name is unchanged — only values
// were retuned (sharper corners, flatter surfaces, hairline borders) plus new additive keys
// (statCardStyle, microLabelStyle, tableStyles, alertBannerStyle, new COLORS/TYPE entries).
// Nothing here renames or removes a key that existed before this pass.

export const COLORS = {
  primary: '#0B5BA1',
  primaryHover: '#0A4A87',
  primaryBg: '#EEF5FC',
  success: '#1E9E5A',
  successBg: '#E9F8EF',
  successBorder: '#BFEBD1',
  danger: '#E0343F',
  dangerBg: '#FDECEC',
  dangerBorder: '#F7C9CB',
  warning: '#DB8A1A',
  warningBg: '#FDF3E2',
  warningBorder: '#F5DDAE',
  // One genuinely distinct semantic state with no existing token: an estimate that's been
  // converted into a work order — not success/danger/warning, its own "moved on" meaning.
  // Consolidated from 2 files that both independently hardcoded the identical #7B68EE.
  accentPurple: '#7B68EE',
  // Tinted background pairs / extra KPI accent extracted from DashboardHome's inline literals
  // (Phase 3). accentPurpleBg is the "picking up parts" segment badge ground (9 files);
  // accentTopCyan is the Total Customers stat card's top bar (2 files).
  accentPurpleBg: '#F5F3FF',
  accentTopCyan: '#0891B2',
  // The dark half of each semantic pair — text, icons and outlines that sit ON a tinted ground.
  // These had been hand-rolled at ~80 sites: #B26B00/#B45309/#8A5A00/#7A5C00/#8A6D00 were five
  // different shades of the same "amber text on amber tint", across 12 files.
  // Darkened from #B26B00 (2026-09). The lighter value cleared 4.5:1 on neither white (4.20:1)
  // nor warningBg (3.82:1), so every amber-on-tint site in the app was failing AA — including
  // the outlined amber actions other work was told to match. #8A5500 clears both by more than
  // a point (6.21:1 on white, 5.65:1 on warningBg), which holds up on a dim screen or in
  // sunlight; the two lighter candidates cleared by under 0.3.
  warningText: '#8A5500',
  successText: '#1A7A3C',
  // Same job as warningText/successText, added for the branch buttons: COLORS.danger (#E0343F) is
  // a FILL colour and only reaches 3.89:1 as text on dangerBg, so a red tinted button needed its
  // own darker text value. #B3261E clears both grounds by more than a point (5.72:1 on dangerBg,
  // 6.54:1 on white) — the same margin standard warningText was chosen against, so it survives a
  // dim screen or direct sunlight rather than passing on paper and failing in a driveway.
  dangerText: '#B3261E',
  accentPurpleText: '#5A4BC4',
  accentTeal: '#0E7C86',
  // Tint borders, completing the *Bg/*Border pairs the semantic colours already had.
  primaryBorder: '#D0E8FF',
  accentPurpleBorder: '#E0D9FF',
  // Solid-button hover fills, matching the existing primaryHover.
  dangerHover: '#C82333',
  successHover: '#25A347',
  neutralHover: '#D8D8D8',
  // A disabled control's label/icon.
  textDisabled: '#C4CBD3',
  // Map markers that mark a POSITION rather than a job — the history scrub handle on the
  // admin map and the technician's own "you are here" dot. Deliberately not a status colour
  // and not the brand blue: its whole job is to be unmistakable from a work-order pin. Was
  // #00BCD4 in TechnicianMap and #00B8D9 in TechnicianDayMap — the same decision made twice,
  // one imperceptible step apart. #00B8D9 wins on contrast over light map tiles (2.17:1 vs
  // 2.10:1) and is the survivor.
  mapPositionMarker: '#00B8D9',
  text: '#161B22',
  textMuted: '#5B6672',
  textFaint: '#8B96A3',
  border: '#E2E6EA',
  bgInput: '#FFFFFF',
  bgPage: '#F5F6F8',
  white: '#FFFFFF',
  // KPI stat-card top accent bars (Ops Console pattern — a colored top edge, not a left border).
  accentTopBlue: '#0B5BA1',
  accentTopGreen: '#1E9E5A',
  accentTopAmber: '#DB8A1A',
  accentTopRed: '#E0343F',
  // Table row hover / header background.
  rowHover: '#F8FAFC',
  tableHeaderBg: '#FFFFFF',
  // Sidebar — kept in sync by hand with Dashboard.css's :root custom properties (that file
  // predates this one and can't import a JS module).
  sidebarBg: '#0F141B',
  sidebarBgAlt: '#161C26',
  sidebarActive: '#0B5BA1',
  sidebarHover: 'rgba(255,255,255,0.06)',
  sidebarText: 'rgba(255,255,255,0.68)',
  sidebarTextMuted: 'rgba(255,255,255,0.36)',
  // An alert dot/badge ON the dark sidebar. COLORS.danger (#E0343F) is tuned for a white
  // ground and reads muddy on #0F141B — this is its dark-surface counterpart.
  sidebarDanger: '#FF8A90'
};

// The iOS/Android minimum touch target. Exported so a style function can reach it too:
// togglePillStyle, inputStyle and filterSelectStyle are called directly from ~48 page files with
// no component in between, so they cannot read useIsMobile. They set minHeight to a CSS custom
// property instead — index.css flips it to TAP_MIN under the same 768px breakpoint the hook uses,
// which needs no call-site change and responds to a rotation without a re-render.
// The four hardware safe-area insets, as CSS values usable in any inline style. The variables
// themselves are defined once in index.css (NOT Dashboard.css — that one is lazy-loaded and a
// technician never mounts it, which is exactly how a previous attempt at this shipped broken).
//
// Any element pinned to a viewport edge with position: fixed must account for these, because
// index.html sets viewport-fit=cover: the viewport extends underneath the status bar, the Dynamic
// Island and the home indicator. An overlay that pins to top: 0 puts its close button under the
// clock, where it cannot be tapped. Use SAFE_INSET rather than writing env() or var() by hand, so
// there is one place to look and one place to change.
export const SAFE_INSET = {
  top: 'var(--safe-top, 0px)',
  right: 'var(--safe-right, 0px)',
  bottom: 'var(--safe-bottom, 0px)',
  left: 'var(--safe-left, 0px)'
};

// Padding for a full-bleed mobile overlay — a sheet that covers the whole screen and therefore
// owns every edge. On desktop the overlay is a centered card with its own margin, so it needs
// none of this.
export const safeAreaPadding = (isMobile, desktopPadding = 0) => (isMobile
  ? {
    paddingTop: SAFE_INSET.top,
    paddingRight: SAFE_INSET.right,
    paddingBottom: SAFE_INSET.bottom,
    paddingLeft: SAFE_INSET.left,
    boxSizing: 'border-box'
  }
  : { padding: desktopPadding });

export const TAP_MIN = '44px';
export const TAP_MIN_VAR = 'var(--tap-min-h, auto)';

export const RADIUS = {
  sm: '4px', // buttons, badges, small controls
  md: '6px', // inputs, cards, table containers, dropdown menus
  lg: '8px'  // modals, page-level panels ONLY
};

// Flat surfaces at rest (card = none, border does the work); elevation reserved for floating
// surfaces (dropdowns, popovers, modals).
export const SHADOW = {
  card: 'none',
  raised: '0 4px 12px rgba(16,24,40,0.10), 0 1px 3px rgba(16,24,40,0.06)',
  // The photo lightbox's own controls (nav arrows, close button, filename caption) float over a
  // near-black scrim, needing a heavier shadow than `raised` to stay legible — reused identically
  // (same literal value) by WorkOrderDetail.js's and CustomerRecord.js's separate lightbox copies
  // before being tokenized here.
  lightbox: '0 4px 16px rgba(0, 0, 0, 0.5)',
  // A dialog sitting on the dimmed backdrop. Heavier than `raised` (which is for a card in the
  // page flow) and lighter than `lightbox` (which fights a near-black scrim) — it was the last
  // untokenized shadow in the app, hardcoded in Modal.js.
  modal: '0 20px 60px rgba(0, 0, 0, 0.3)'
};

// Collapses the ~13 arbitrary maxWidth values and ~7 arbitrary z-index values that had drifted
// across every hand-rolled modal in the app (see components/Modal.js) onto a clean scale and a
// 3-tier stacking system: 'modal' for anything opened directly from a page, 'modalNested' for
// one opened from inside another already-open modal, 'modalTop' for always-on-top cases (voice
// command, payment, public approval links).
export const MODAL_WIDTH = { sm: '420px', md: '520px', lg: '640px', xl: '760px', xxl: '960px' };
// modalCritical sits above CustomerPortalDashboard.js's full-page takeover (zIndex 3000/3001,
// a deliberately different, out-of-scope UI pattern — see components/Modal.js) — the one modal
// that needs to render on top of it (JobReviewForm.js, prompted from within that portal).
export const Z_INDEX = { modal: 1000, modalNested: 1100, modalTop: 2000, modalCritical: 3500 };

export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px'
};

// Inter (loaded in public/index.html) — falls through to the same system stack as before if it
// hasn't loaded yet, so nothing shifts layout on a slow connection.
export const TYPE = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  h2: '20px',
  h3: '16px',
  body: '13px',
  label: '12px',
  caption: '11px',
  statNumber: '28px',
  pageTitle: '22px'
};

export const cardStyle = (overrides = {}) => ({
  backgroundColor: COLORS.white,
  padding: '16px 20px',
  borderRadius: RADIUS.md,
  border: `1px solid ${COLORS.border}`,
  boxShadow: SHADOW.card,
  marginBottom: SPACING.lg,
  ...overrides
});

export const inputStyle = (overrides = {}) => ({
  width: '100%',
  padding: '9px 12px',
  minHeight: TAP_MIN_VAR,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  fontSize: '14px',
  backgroundColor: COLORS.bgInput,
  boxSizing: 'border-box',
  ...overrides
});

export const labelStyle = (overrides = {}) => ({
  display: 'block',
  marginBottom: '6px',
  fontSize: TYPE.label,
  fontWeight: '600',
  color: COLORS.textMuted,
  ...overrides
});

// A small uppercase heading caption ("ACCOUNT", "COMPANY INFO") for grouping fields within a
// tab/card — distinct from labelStyle() (that's for a single form field's own label, not
// uppercase/no letter-spacing) and from the one-off colored status-pill literal in
// FeaturesPluginsSettings.js (that's a badge with a background, not a heading).
export const sectionLabelStyle = (overrides = {}) => ({
  fontSize: TYPE.caption,
  fontWeight: '700',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: COLORS.textFaint,
  marginBottom: SPACING.sm,
  ...overrides
});

// Shared list-card treatment (2026-08 consistency pass) — every list screen's card ends up with
// the same resting/hover elevation and radius; screens with a natural color-coding concept (e.g.
// work order status) pass `borderLeft` to keep their accent bar, screens without one (customers,
// crews, subcontractors) simply omit it and get a plain card.
export const listCardStyle = (overrides = {}) => ({
  backgroundColor: COLORS.white,
  borderRadius: RADIUS.lg,
  border: `1px solid ${COLORS.border}`,
  boxShadow: SHADOW.card,
  ...overrides
});

// A lighter-weight toggle pill for secondary sub-filters (e.g. Daily/Weekly/Monthly, Total/
// Driving/Working) — distinct from TabBar's solid-fill primary look, which is reserved for
// actual page-level navigation. Modeled on TimeManagementCombined.js's Daily/Weekly/Monthly row,
// which already had this right; its Total/Driving/Working row previously drifted to a different
// (wrong) treatment and is brought in line with this instead.
export const togglePillStyle = (active, overrides = {}) => ({
  padding: '10px 16px',
  minHeight: TAP_MIN_VAR,
  boxSizing: 'border-box',
  backgroundColor: active ? COLORS.primaryBg : COLORS.white,
  color: active ? COLORS.primary : COLORS.textMuted,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  fontSize: TYPE.label,
  fontWeight: active ? '600' : '500',
  ...overrides
});

// A plain filter <select> (Store #, Sort, etc.) in a list screen's toolbar — was typed out as an
// identical inline object at 2+ call sites in WorkOrderList.js alone before being tokenized here.
export const filterSelectStyle = (overrides = {}) => ({
  padding: '10px 14px',
  minHeight: TAP_MIN_VAR,
  boxSizing: 'border-box',
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  fontSize: TYPE.body,
  backgroundColor: COLORS.white,
  fontWeight: '600',
  color: COLORS.text,
  ...overrides
});

// KPI stat card with colored top accent bar — the standard dashboard metric card (Ops Console
// pattern: a 3px colored TOP edge, not a left border, distinguishing it from listCardStyle).
//
// Deliberately four separate border-side longhands (borderTop/Right/Bottom/Left) instead of a
// `border` shorthand + `borderTop` override — real bug found in production: when a caller's
// `overrides` changes only the non-top sides on re-render (e.g. StatCard.js's hover state,
// TechnicianWorkOrders.js's `active` state), a `border` shorthand in the merged style object
// resets ALL FOUR sides at the DOM level when React applies it, but React's own style-prop
// diffing only re-applies a key whose VALUE changed since the last render — so if `borderTop`'s
// value happens to be unchanged, React skips re-applying it even though the shorthand write for
// `border` just silently clobbered it in the browser. The colored top bar would vanish on
// hover/click and only come back on a full remount (page refresh). Longhands sidestep this
// entirely since each side is its own independent style key with no shorthand to collide with —
// any caller overriding non-top sides MUST do so via borderLeft/Right/Bottom, never `border`.
export const statCardStyle = (accentColor, overrides = {}) => ({
  backgroundColor: COLORS.white,
  borderTop: `3px solid ${accentColor || COLORS.primary}`,
  borderRight: `1px solid ${COLORS.border}`,
  borderBottom: `1px solid ${COLORS.border}`,
  borderLeft: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  padding: '16px 20px',
  ...overrides
});

// Uppercase micro-label used for KPI labels and in-card section headings — same shape as
// sectionLabelStyle() above (kept as a separate export per the Ops Console spec rather than
// merged, since renaming/removing an existing key isn't allowed).
export const microLabelStyle = (overrides = {}) => ({
  fontSize: '11px',
  fontWeight: '700',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: COLORS.textFaint,
  ...overrides
});

// Standard data-table styles — use for every list of records app-wide (Ops Console: tables over
// stacked cards for records).
export const tableStyles = {
  container: { backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: COLORS.textFaint, borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white },
  td: { padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text, verticalAlign: 'middle' },
  rowHover: { backgroundColor: COLORS.rowHover }
};

// Inline alert banner (Action Items pattern) — soft tinted bg, small colored marker, link on
// second line.
export const alertBannerStyle = (kind, overrides = {}) => {
  const map = {
    danger: { bg: COLORS.dangerBg, border: COLORS.dangerBorder },
    warning: { bg: COLORS.warningBg, border: COLORS.warningBorder },
    success: { bg: COLORS.successBg, border: COLORS.successBorder },
    info: { bg: COLORS.primaryBg, border: COLORS.border }
  };
  const c = map[kind] || map.info;
  return { backgroundColor: c.bg, border: `1px solid ${c.border}`, borderRadius: RADIUS.md, padding: '14px 16px', marginBottom: '10px', ...overrides };
};

const VARIANT_STYLES = {
  primary: { backgroundColor: COLORS.primary, color: COLORS.white, border: 'none' },
  secondary: { backgroundColor: COLORS.white, color: COLORS.primary, border: `1.5px solid ${COLORS.primary}` },
  danger: { backgroundColor: COLORS.white, color: COLORS.danger, border: `1.5px solid ${COLORS.danger}` },
  dangerSolid: { backgroundColor: COLORS.danger, color: COLORS.white, border: 'none' },
  success: { backgroundColor: COLORS.success, color: COLORS.white, border: 'none' },
  // The quiet row action (Edit / Copy Link / Download PDF next to a record) — a plain
  // bordered chip, not a branded outline. It was hand-rolled at ~20 sites before Phase 3.
  neutral: { backgroundColor: COLORS.white, color: COLORS.textMuted, border: `1px solid ${COLORS.border}` },
  ghost: { backgroundColor: 'transparent', color: COLORS.textMuted, border: 'none' }
};

// Vertical padding bumped +5px on both sizes (2026-08 touch-target pass, confirmed via a real
// before/after comparison) — the previous values rendered at roughly 30-32px (md) / 26-28px (sm)
// tall, under the ~44px usually recommended for a touch target on phones. Horizontal padding and
// fontSize untouched, so button width/text size don't shift, only the vertical breathing room.
// Desktop keeps the dense values Filipp signed off on. The *Mobile counterparts exist because a
// 42px / 36px control is under the 44px touch target, and this app is used on phones in the
// field all day — 16 of 16 tappable elements on the technician's schedule measured under 44px
// before Phase 5. Components pick the variant via useIsMobile; no call site passes it.
const SIZE_STYLES = {
  md: { padding: '13px 16px', fontSize: '13px' },
  sm: { padding: '11px 12px', fontSize: '12px' },
  mdMobile: { padding: '13px 18px', fontSize: '14px', minHeight: TAP_MIN },
  smMobile: { padding: '12px 14px', fontSize: '13px', minHeight: TAP_MIN }
};

// A component that already reads useIsMobile maps its own size through this.
export const mobileSize = (size) => (size === 'sm' ? 'smMobile' : 'mdMobile');

// buttonStyle(variant, size) covers the plain-object case (spread into style={{}}); Button.js
// wraps this for the common case, but some call sites need the raw object (e.g. hover handlers
// that swap colors imperatively).
export const buttonStyle = (variant = 'primary', size = 'md', overrides = {}) => ({
  ...SIZE_STYLES[size],
  ...VARIANT_STYLES[variant],
  borderRadius: RADIUS.sm,
  cursor: 'pointer',
  fontWeight: '600',
  whiteSpace: 'nowrap',
  transition: 'background-color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .1s ease',
  ...overrides
});
